import { CustomerType } from '@brighte/validation';
import { FinancialProduct } from '../../graphql';
import { isJointApplicant, productIds } from '../../utils';

export const defaultFinanceTermsList = ['beOver18', 'haveAustralianIncome'];

export const defaultPrivacyTermsList = [
  'consentPrivacyPolicy',
  'consentCreditReport',
  'creditAndTermsAgreement',
  'haveReadDisclosureOfCreditInformation',
];

export const bheslPrivacyTermsList = [
  ...defaultPrivacyTermsList.filter(term => term !== 'creditAndTermsAgreement'),
  'creditAndTermsBheslAgreement',
  'consentNswTerms',
];

export const e2515LoanPrivacyTermsList = [
  'consentElectrify2515Terms',
  'consentPrivacyPolicy',
  'creditAndTermsAgreement',
];

export const businessTermsList = ['abn', 'grantLockLetter', 'proofOfConsent', 'proofOfAbility', 'bankAccount'];
export const smeTermsList = [
  'businessTrading2Years',
  'businessBankAccount',
  'businessNoJudgements',
  'businessValidABNGST',
  'applicantPermanentResident',
  'applicantOver18',
  'businessNoBankruptcies',
  'guarantorOwnsHome',
  'businessNoLiquidation',
  'businessNoTaxDebt',
];

export const actCommunityTermsList = [
  'actCommunityNotForProfit',
  'actCommunityHaveAbn',
  'actCommunityBoardRep',
  'actCommunityIncorporatedStructure',
  'actCommunityPurchaseForACT',
  'actCommunityParticipateProgram',
  'businessBankAccount',
  'actCommunityApplicantsMust',
];

export const requirementsList = {
  [productIds.ACT_SLIP]: ['needProofTypes', 'needIncomeProof', 'needConcessionProof', 'needBiometricCheck'],
  [productIds.FLEX]: ['needBiometricProofTypes', 'needBiometricCheck'],
  [productIds.BRIGHTE_PAY]: ['needProofTypes', 'needIncomeProofOptional', 'needBiometricCheckOptional'],
  [productIds.ELECTRIFY_2515]: ['needProofTypes', 'needIncomeProof', 'needBiometricCheckOptional'],
  default: ['needProofTypes', 'needIncomeProof', 'needBiometricCheck'],
};

export const getFinanceTermsByFinancialProduct = (financialProduct: string) => {
  let financialProductList: string[] = [];
  const isJoint = isJointApplicant();
  switch (financialProduct) {
    case productIds.ACT_SHS:
      financialProductList = [
        ...defaultFinanceTermsList,
        'beActHomeOwner',
        'beCompletedPropertyOwner',
        'consentActTerms',
      ];
      break;
    case productIds.ACT_SLIP:
      {
        const filteredTerms = defaultFinanceTermsList.filter(item => item !== 'haveAustralianIncome');
        financialProductList = [...filteredTerms, 'beActHomeOwnerAndOccupant', 'consentActTerms'];
      }
      break;
    case productIds.TAS_ESLS:
      financialProductList = [...defaultFinanceTermsList, 'beTasHomeOwner'];
      break;
    case productIds.ACT_SFA:
      financialProductList = [...businessTermsList];
      break;
    case productIds.FLEX:
      financialProductList = isJoint
        ? ['beOver18', 'haveAustralianIncome', 'haveActiveMortgage', 'haveEligibleNetIncome']
        : ['beOver18', 'haveAustralianIncomeWithoutRetired', 'haveActiveMortgage', 'haveEligibleNetIncome'];
      break;
    case productIds.BRIGHTE_PAY:
      financialProductList = ['beOver18', 'haveAustralianIncome'];
      break;
    case productIds.SME:
      financialProductList = [...smeTermsList];
      break;
    case productIds.ACT_COMMUNITY:
      financialProductList = [...actCommunityTermsList];
      break;
    case productIds.BHESL:
      financialProductList = [...defaultFinanceTermsList];
      break;
    default:
      financialProductList = [...defaultFinanceTermsList];
      break;
  }
  return financialProductList;
};

export const getPrivacyTermsByFinancialProduct = (financialProduct: string, isCashApplication: boolean = false) => {
  let financialProductList: string[] = [];
  switch (financialProduct) {
    case productIds.TAS_ESLS:
      financialProductList = ['consentTasTerms', ...defaultPrivacyTermsList];
      break;
    case productIds.ACT_SFA:
      financialProductList = [];
      break;
    case productIds.FLEX:
      financialProductList = [
        'consentPrivacyPolicy',
        'creditAndTermsFlexAgreement',
        'consentFlexCreditCheck',
        'haveReadDisclosureOfCreditInformation',
      ];
      break;
    case productIds.ELECTRIFY_2515:
      if (isCashApplication) {
        financialProductList = [
          'consentElectrify2515Terms',
          'consentPrivacyPolicy',
          'consentCashCreditReport',
          'creditAndTermsAgreement',
          'haveReadDisclosureOfCreditInformation',
        ];
      } else {
        financialProductList = e2515LoanPrivacyTermsList;
      }
      break;
    case productIds.BRIGHTE_PAY:
      financialProductList = [
        'consentPrivacyPolicy',
        'creditAndTermsBNPLAgreement',
        'consentCreditReportBNPL',
        'haveReadDisclosureOfCreditInformation',
      ];
      break;
    case productIds.SME:
    case productIds.ACT_COMMUNITY:
      financialProductList = [];
      break;
    case productIds.BHESL:
      financialProductList = [...bheslPrivacyTermsList];
      break;
    default:
      financialProductList = [...defaultPrivacyTermsList];
      break;
  }

  return financialProductList;
};

export const getRequirements = (financialProductId: string): string[] => {
  return requirementsList[financialProductId] || requirementsList.default;
};

export const getTermsInputByFinancialProduct = (financialProduct: FinancialProduct, isCashApplication?: boolean) => {
  if (financialProduct.customerType === CustomerType.Business) {
    return [
      'businessEligibilityCriteriaAgreement',
      ...getPrivacyTermsByFinancialProduct(financialProduct.id, isCashApplication),
    ];
  }

  return [
    ...getFinanceTermsByFinancialProduct(financialProduct.id),
    ...getPrivacyTermsByFinancialProduct(financialProduct.id, isCashApplication),
  ];
};
